import { useEffect, useRef, useState } from 'react';
import { api, type SubjectType, type ThreadTaskSuggestion } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import ChatMarkdown from './ChatMarkdown';
import { Card, CardHeader, CardTitle, CardContent, Button } from './ui';
import { ChatBox, SendButton, CHAT_INPUT } from './chat/ChatBox';
import { ASSISTANT_BUBBLE, BUBBLE, USER_BUBBLE } from './chat/bubbles';

interface Message {
  id: string;
  author_id: string | null;
  kind: 'human' | 'ai_answer' | 'ai_volunteer';
  body: string;
  suggestion_data: ThreadTaskSuggestion | null;
  applied: boolean;
  created_at: string;
}

const POLL_MS = 20000;

/**
 * The conversation for one property.
 *
 * No websockets by design: two people at low volume, so polling on mount and every 20s is
 * enough, and a realtime layer is a large amount of machinery to maintain for a two-person
 * chat.
 *
 * Polling is keyed on the `threadId` state itself, not started only after an initial load
 * that finds an existing thread. A thread is created lazily on the first message, so on a
 * property nobody has discussed yet, the initial load finds nothing and threadId stays null
 * until send() below creates one. If the interval were only ever started inside that initial
 * load, a lazily created thread would never be polled for the rest of the session - the other
 * person's replies would not show up until a page reload, on exactly the conversation most
 * likely to be active since someone just started it. Keying the effect on threadId instead
 * means the interval starts whenever a thread comes to exist, however that happened, and the
 * effect's own cleanup guarantees only one interval is ever alive for this panel.
 */
export default function ThreadPanel({
  subjectType,
  subjectId,
}: {
  subjectType: SubjectType;
  subjectId: string;
}) {
  const { user } = useAuth();
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [applying, setApplying] = useState<string | null>(null);
  // Suggestions accepted during this session, held alongside the server's own `applied` flag.
  // See accept() below for why the local copy is load-bearing rather than belt-and-braces.
  const [appliedNow, setAppliedNow] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Flipped false on unmount only. api.ts has no request cancellation, so in-flight requests
  // (a poll, a send) keep running after the user navigates away - which is correct, a send in
  // particular must not be abandoned mid-flight or it can create a thread with no message in
  // it. What must not happen is a setState call landing on an unmounted component, so every
  // setState below that follows an await checks this first.
  const mountedRef = useRef(true);
  useEffect(() => {
    // This assignment looks redundant - the ref already starts true - but it is load-bearing.
    // React.StrictMode (frontend/src/main.tsx) double-invokes effects on mount in development:
    // setup, cleanup, setup. The simulated cleanup below sets mountedRef.current to false; if
    // this second setup did not put it back to true, the ref would stay false for the rest of
    // the component's life, and every guard above that checks it would silently no-op forever -
    // including the one in send() that stores a lazily created threadId. That breaks polling
    // (it never starts) and lazy creation (every send creates a new orphaned thread, since the
    // component never learns a thread now exists). Do not remove this line.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * UI3a - the read RECEIPT can no longer report that something else failed.
   *
   * ⛔⛔ IT WAS `await api.markThreadRead(id)` BARE, and this function has FOUR callers, three of
   * which surface a failure to the operator. Measured by watching it happen: with the receipt
   * failing, the initial load printed **"Request failed" over a conversation that had loaded
   * perfectly** - his real message visible on screen underneath the banner.
   *
   * ⭐⭐⭐ AND THE NOTE THAT FILED THIS UNDER-STATED IT. The two worse callers are the WRITES:
   * `send()` catches into **"Could not send"** and `accept()` into **"Could not accept"**, both
   * AFTER their POST has already succeeded - so a failed receipt told him a message he had sent
   * had not sent. That is not a cosmetic banner, it is the app lying about a write.
   *
   * ⭐ Marking-as-read is BOOKKEEPING. The whole cost of it failing is that the unread badge
   * stays up until the next poll, which is self-correcting; it is never worth failing the thing
   * the operator actually did. Logged, never surfaced - a silently swallowed backend fault is
   * how a real problem hides, so it goes to the console rather than nowhere.
   * ⚠️ The poll's own `.catch(() => {})` stays: it guards `getThreadMessages`, which is a
   * different failure and still must not raise a banner from a background timer.
   */
  const loadMessages = async (id: string) => {
    const res = await api.getThreadMessages(id);
    if (!mountedRef.current) return;
    setMessages(res.messages);
    try {
      await api.markThreadRead(id);
    } catch (err: unknown) {
      // ⭐ The MESSAGE, not the Error: a stack trace for a piece of bookkeeping is noise in the
      // console and in every test run that exercises this path. It still names the fault.
      console.warn(
        'markThreadRead failed; the unread badge will clear on the next read:',
        err instanceof Error ? err.message : err,
      );
    }
  };

  // Initial load: find (never create) the thread for this subject, and load its messages if
  // one exists. State resets synchronously up front so that navigating between subjects on a
  // route that reuses this component (property A to property B without an unmount) never
  // leaves property A's messages on screen while property B's fetch is still in flight.
  useEffect(() => {
    let cancelled = false;
    setThreadId(null);
    setMessages([]);
    setError('');

    (async () => {
      try {
        const res = await api.getThreadsForSubject(subjectType, subjectId);
        if (cancelled) return;
        const existing = res.threads[0]?.id ?? null;
        setThreadId(existing);
        if (existing) {
          await loadMessages(existing);
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the conversation');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [subjectType, subjectId]);

  // Poll while a thread exists. Re-running this effect on every threadId change (null -> an
  // id, either from the initial load above or from a lazy create in send() below) is what
  // fixes the defect described in the doc comment: whichever path sets threadId, this effect
  // sees it and starts polling. The cleanup clears the previous interval before a new one is
  // ever created, so there is never more than one running at a time.
  useEffect(() => {
    if (!threadId) return;
    // Caught and dropped, deliberately: the initial load above surfaces a failure via
    // `error` because the user is actively waiting on it, but a background poll failing
    // quietly is the right behaviour - a transient 500, or a 404 from markThreadRead on a
    // thread that got cleaned up, should not throw a banner over a conversation that is
    // otherwise rendering fine. Without this catch, a persistent failure raises an
    // unhandled promise rejection every POLL_MS.
    const timer = setInterval(() => void loadMessages(threadId).catch(() => {}), POLL_MS);
    return () => clearInterval(timer);
  }, [threadId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    setError('');
    try {
      // Resolved lazily, and server-side: a property you never discuss never gets an empty
      // thread just from being viewed (the initial load above only ever lists, it never
      // creates), and asking the server for "the" thread here - rather than trusting this
      // component's own possibly-stale "I have no threadId" state - is what keeps two
      // people composing on the same undiscussed property from creating two separate
      // threads. See getOrCreateThreadForSubject's doc comment in threadService.ts.
      let id = threadId;
      if (!id) {
        const resolved = await api.getOrCreateThreadForSubject(subjectType, subjectId);
        id = resolved.thread.id;
        if (mountedRef.current) setThreadId(id);
      }
      await api.postThreadMessage(id, body);
      if (mountedRef.current) setDraft('');
      await loadMessages(id);
    } catch (err: unknown) {
      if (mountedRef.current) setError(err instanceof Error ? err.message : 'Could not send');
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  const accept = async (messageId: string) => {
    setApplying(messageId);
    setError('');
    try {
      await api.applySuggestion(messageId);
      // Marked locally as well as re-fetched: the poll above is on a 20s interval, and the
      // card must stop offering an accept the instant it succeeds or the user clicks twice
      // and gets a 409 for their trouble.
      if (mountedRef.current) setAppliedNow((prev) => new Set(prev).add(messageId));
      if (threadId) await loadMessages(threadId);
    } catch (err: unknown) {
      if (mountedRef.current) setError(err instanceof Error ? err.message : 'Could not accept');
    } finally {
      if (mountedRef.current) setApplying(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            {error}
          </div>
        )}

        <div className="max-h-96 overflow-y-auto space-y-2 pr-1">
          {messages.length === 0 && (
            <p className="text-sm text-gray-500">
              No messages yet. Anything you post here stays attached to this property.
            </p>
          )}
          {messages.map((m) => {
            const mine = m.author_id === user?.id;
            // Keyed on `kind`, NOT on `author_id === null`. Those agree only because the AI
            // always posts with both together, and an answer to a direct question has to
            // render differently from an unprompted comment (Phase C) - which a null author
            // cannot distinguish at all. Backlog B12.
            const isAi = m.kind !== 'human';
            const suggestion = m.suggestion_data;
            const isApplied = m.applied || appliedNow.has(m.id);
            return (
              <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                {/* CHAT-DUET (his pick, 2026-10-28): the chat's bubbles - his soft red, Robert's light grey; the AI's
                    answer and a volunteered note keep their blue and amber washes (those colours mean something). */}
                <div
                  data-bubble={isAi ? 'assistant' : mine ? 'user' : 'other'}
                  className={`max-w-[85%] ${
                    m.kind === 'ai_volunteer'
                      ? `${BUBBLE} bg-warning-wash text-warning border border-warning/30`
                      : isAi
                        ? `${BUBBLE} bg-info-wash text-info border border-info/30`
                        : mine
                          ? USER_BUBBLE
                          : ASSISTANT_BUBBLE
                  }`}
                >
                  {isAi && (
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide opacity-70">
                      {m.kind === 'ai_volunteer' ? 'Assistant noticed' : 'Assistant'}
                    </p>
                  )}
                  {/* ⛔ Split on `isAi`, the SAME `kind`-derived flag the bubble colour uses -
                      never on `author_id === null`, for the reason given above. The assistant's
                      answer is markdown and gets rendered; Robert's and Joseph's own messages
                      keep the pre-wrap they have always had, so their newlines survive and their
                      punctuation is never read as markup. */}
                  {isAi ? (
                    <ChatMarkdown text={m.body} />
                  ) : (
                    <p className="whitespace-pre-wrap">{m.body}</p>
                  )}
                  <p className="mt-1 text-[11px] text-gray-500">
                    {new Date(m.created_at).toLocaleString()}
                  </p>
                </div>

                {suggestion?.title && (
                  <div className="mt-1 max-w-[85%] rounded-[20px] border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                      Suggested task
                    </p>
                    <p className="mt-0.5 font-medium text-gray-900">{suggestion.title}</p>
                    {suggestion.due_date && (
                      <p className="text-xs text-gray-500">Due {suggestion.due_date}</p>
                    )}
                    {isApplied ? (
                      // CHAT-DUET: "the deal's tasks" - the menu's Tasks is the GoHighLevel list, a different one.
                      <p className="mt-1 text-xs font-medium text-success">Added to the deal&apos;s tasks</p>
                    ) : (
                      <Button
                        type="button"
                        className="mt-2"
                        isLoading={applying === m.id}
                        disabled={applying === m.id}
                        onClick={() => accept(m.id)}
                      >
                        Add task
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* CHAT-DUET: the same round box as the Assistant (no + menu - the thread has no files, voice or history). */}
        <form onSubmit={send}>
          <ChatBox>
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Message Robert, or start with @ai to ask the assistant"
              className={`${CHAT_INPUT} pl-3`}
            />
            <SendButton type="submit" busy={sending} disabled={sending || !draft.trim()} />
          </ChatBox>
        </form>
      </CardContent>
    </Card>
  );
}
